import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobCardService } from '../../services/apiService';
import { FaSpinner, FaDownload } from 'react-icons/fa';

const ServiceJobPayment = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const fetchJob = async () => {
    setLoading(true);
    try {
      const jc = await jobCardService.getById(jobId);
      setJob(jc);
    } catch (e) {
      setJob(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const totals = useMemo(() => {
    if (!job?.estimate) return { subtotal: 0, tax: 0, total: 0 };
    const laborItems = job.estimate.laborItems || [];
    const parts = job.estimate.parts || [];

    const laborSub = laborItems.reduce(
      (s, li) => s + (Number(li.hours) || 0) * (Number(li.rate) || 0),
      0
    );

    const partsSub = parts.reduce((s, p) => {
      const qty = Number(p.quantity || 1);
      const unit = Number(p.unitPrice ?? 0);
      const basePrice = unit > 0 ? unit : Number(p.price ?? 0);
      return s + basePrice * qty;
    }, 0);

    const subtotal = laborSub + partsSub;
    const tax = subtotal * 0.18;
    const total = subtotal + tax;

    return { subtotal, tax, total };
  }, [job]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.body.appendChild(script);
    });
  };

  const handlePayNow = async () => {
    try {
      setActionLoading('pay');
      const paymentData = await jobCardService.createPayment(jobId, 'razorpay');
      const { keyId, orderId, amountPaise, amount, currency } = paymentData || {};

      if (keyId && orderId) {
        await loadRazorpayScript();
        if (!window.Razorpay) {
          throw new Error('Payment gateway could not be loaded. Try again or use demo payment.');
        }
        const options = {
          key: keyId,
          amount: amountPaise || Math.round((amount || 0) * 100),
          currency: currency || 'INR',
          name: 'Service Job Payment',
          description: `Job ${job?.jobCardId || jobId}`,
          order_id: orderId,
          handler: async (res) => {
            try {
              await jobCardService.verifyPayment(jobId, {
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              await fetchJob();
              navigate(`/service/job/${jobId}`);
            } catch (err) {
              alert(err.message || 'Payment verification failed');
            } finally {
              setActionLoading('');
            }
          },
          modal: { ondismiss: () => setActionLoading('') },
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', () => {
          setActionLoading('');
          alert('Payment failed or was cancelled.');
        });
        rzp.open();
        return;
      }

      // Fallback: demo webhook when Razorpay is not configured
      await jobCardService.paymentWebhook({
        jobCardId: jobId,
        status: 'success',
        gateway: 'demo',
        transactionId: `TXN-${Math.floor(Math.random() * 1000000)}`,
        amount: job?.invoice?.total || totals.total,
      });
      await fetchJob();
      navigate(`/service/job/${jobId}`);
    } catch (e) {
      alert(e.message || 'Payment failed');
    } finally {
      setActionLoading('');
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      setActionLoading('download');
      await jobCardService.downloadInvoice(jobId);
    } catch (e) {
      alert(e.message || 'Download failed');
    } finally {
      setActionLoading('');
    }
  };

  if (loading || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600 flex items-center gap-2">
          <FaSpinner className="animate-spin" /> Loading payment details…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-8">
      <div className="section-container max-w-3xl mx-auto">
        <button
          onClick={() => navigate(`/service/job/${jobId}`)}
          className="text-red-600 hover:text-red-700 font-medium mb-4 inline-flex items-center gap-1"
        >
          ← Back to Job
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Pay for Service</h1>
              <p className="text-sm text-gray-600 mt-1">
                Job #{(job.jobCardId || job._id || '').toString().slice(-8)} ·{' '}
                {job.vehicle?.make} {job.vehicle?.model} {job.vehicle?.variant}
              </p>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
              {job.status}
            </span>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-semibold">₹{totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tax (18%)</span>
              <span className="font-semibold">₹{totals.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-gray-900 border-t pt-2 mt-1">
              <span>Total payable</span>
              <span>₹{totals.total.toFixed(2)}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handlePayNow}
              disabled={actionLoading === 'pay'}
              className="flex-1 py-3 px-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {actionLoading === 'pay' && <FaSpinner className="animate-spin" />}
              {actionLoading === 'pay' ? 'Processing…' : 'Pay Now'}
            </button>
            <button
              onClick={handleDownloadInvoice}
              disabled={actionLoading === 'download'}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {actionLoading === 'download' && <FaSpinner className="animate-spin" />}
              <FaDownload /> Download Invoice
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceJobPayment;

